import "./index";
import {
  hasPendingStarLaunch,
  readStarLaunchContext,
  readWorldLaunchPresentation,
} from "@zr-eduai/star-launch-element";
import {
  isStarJourneyModuleId,
  type StarJourneyModuleId,
  type StarJourneyModuleSnapshotV2,
  type StarLearningLineId,
} from "@zr-eduai/star-navigation-core";
import { fetchSessionAccount } from "./session";

export type AutoMountConfig = {
  currentApp?: string;
  currentTheme?: "ai-eco" | "ai-sail" | "ai-3d";
  currentEntry?: "world-view" | "course" | "inquiry" | "observation" | "experiment" | "results";
  currentSurface?: "home" | "catalog" | "themes" | "extended-apps";
  navigationMode?: "legacy" | "journey-v2" | "teacher-workspace";
  layout?: "legacy" | "journey";
  currentLine?: StarLearningLineId | (string & {});
  currentModule?: StarJourneyModuleId;
  introAvailable?: boolean;
  helpHref?: string;
  journeySnapshot?: readonly StarJourneyModuleSnapshotV2[];
  theme?: string;
  starlinkBaseUrl?: string;
  ecoBaseUrl?: string;
  parentHref?: string;
  parentLabel?: string;
  allowedApps?: string | readonly string[];
  maintenanceApps?: string | readonly string[];
  barBackground?: string;
  barForeground?: string;
  barBorder?: string;
  accountName?: string;
  accountRole?: string;
  accountCredits?: number | string;
  accountValidity?: string;
  accountPermissions?: string | readonly string[];
  adminHref?: string;
  printingHref?: string;
  loginHref?: string;
  loginLabel?: string;
  sessionEndpoint?: string;
  logoutEndpoint?: string;
  logoutRedirect?: string;
};

declare global {
  interface Window {
    __STARLINK_NAVIGATION__?: AutoMountConfig;
  }
}

const autoMountScript = document.currentScript as HTMLScriptElement | null;
const delegatedLaunch = new URLSearchParams(window.location.search).has("handoff");
const storedConsoleLaunch = sessionStorage.getItem("consoleLaunch");
const storedWorldLaunch = sessionStorage.getItem("worldAppLaunch");
const pendingLaunch = hasPendingStarLaunch(
  window.location.search,
  storedConsoleLaunch || storedWorldLaunch,
);
if (pendingLaunch) document.documentElement.dataset.starLaunchActive = "true";

function storedConsolePresentation() {
  const direct = readStarLaunchContext(window.location.search);
  if (direct.active || !storedConsoleLaunch) return direct;
  try {
    const stored = JSON.parse(storedConsoleLaunch) as { progress?: number; startedAt?: number; expiresAt?: number };
    return {
      active: Boolean(stored.expiresAt && stored.expiresAt > Date.now()),
      progress: Number.isFinite(stored.progress) ? Number(stored.progress) : 72,
      startedAt: Number.isFinite(stored.startedAt) ? Number(stored.startedAt) : null,
    };
  } catch {
    return direct;
  }
}

function mountPrebootLaunch() {
  if (!pendingLaunch || !document.body || document.querySelector("star-launch-surface")) return;
  const currentApp = autoMountScript?.dataset.currentApp || window.__STARLINK_NAVIGATION__?.currentApp;
  if (!currentApp) return;
  const world = readWorldLaunchPresentation(window.location.search, storedWorldLaunch);
  const context = world.active ? world : storedConsolePresentation();
  const surface = document.createElement("star-launch-surface");
  surface.dataset.starLaunchPreboot = "true";
  surface.setAttribute("app-id", currentApp);
  surface.setAttribute("progress", String(context.progress));
  if (context.startedAt) surface.setAttribute("started-at", String(context.startedAt));
  surface.setAttribute("phase", "正在进入应用");
  surface.setAttribute("play-video", "");
  document.body.append(surface);
}

if (pendingLaunch) {
  if (document.body) mountPrebootLaunch();
  else document.addEventListener("DOMContentLoaded", mountPrebootLaunch, { once: true });
}

function stringValue(value: string | readonly string[] | undefined): string | undefined {
  return typeof value === "string" ? value : value?.join(",");
}

function queryContextValue(name: "starTheme" | "starEntry"): string | undefined {
  return new URLSearchParams(window.location.search).get(name) || undefined;
}

const legacyEntryModules: Readonly<Record<string, StarJourneyModuleId>> = {
  observation: "observation",
  experiment: "experiment",
  inquiry: "inquiry",
  course: "course",
  results: "showcase",
};

function queryJourneyModule(): StarJourneyModuleId | undefined {
  const query = new URLSearchParams(window.location.search);
  if (query.has("starModule")) {
    const currentModule = query.get("starModule");
    return isStarJourneyModuleId(currentModule) ? currentModule : undefined;
  }
  const legacyEntry = query.get("starEntry");
  return legacyEntry ? legacyEntryModules[legacyEntry] : undefined;
}

function mount() {
  if (document.querySelector("starlink-navigation")) return;
  const config = window.__STARLINK_NAVIGATION__ ?? {};
  const element = document.createElement("starlink-navigation");
  const allowedApps = autoMountScript?.hasAttribute("data-allowed-apps")
    ? autoMountScript.dataset.allowedApps ?? ""
    : stringValue(config.allowedApps);
  const attributes: Record<string, string | undefined> = {
    "current-app": autoMountScript?.dataset.currentApp || config.currentApp,
    "current-theme": autoMountScript?.dataset.currentTheme || config.currentTheme || queryContextValue("starTheme"),
    "current-entry": autoMountScript?.dataset.currentEntry || config.currentEntry || queryContextValue("starEntry"),
    "current-surface": autoMountScript?.dataset.currentSurface || config.currentSurface,
    "navigation-mode": autoMountScript?.dataset.navigationMode || config.navigationMode,
    "teacher-section": autoMountScript?.dataset.teacherSection,
    layout: autoMountScript?.dataset.layout || config.layout,
    "current-line": autoMountScript?.dataset.currentLine || config.currentLine || queryContextValue("starTheme"),
    "current-module": autoMountScript?.dataset.currentModule || config.currentModule || queryJourneyModule(),
    "intro-available": autoMountScript?.hasAttribute("data-intro-available") || config.introAvailable ? "" : undefined,
    "help-href": autoMountScript?.dataset.helpHref || config.helpHref,
    theme: autoMountScript?.dataset.theme || config.theme,
    "starlink-base-url": autoMountScript?.dataset.starlinkBaseUrl || config.starlinkBaseUrl,
    "eco-base-url": autoMountScript?.dataset.ecoBaseUrl || config.ecoBaseUrl,
    "parent-href": autoMountScript?.dataset.parentHref || config.parentHref,
    "parent-label": autoMountScript?.dataset.parentLabel || config.parentLabel,
    "allowed-apps": allowedApps,
    "maintenance-apps": autoMountScript?.dataset.maintenanceApps || stringValue(config.maintenanceApps),
    "bar-background": autoMountScript?.dataset.barBackground || config.barBackground,
    "bar-foreground": autoMountScript?.dataset.barForeground || config.barForeground,
    "bar-border": autoMountScript?.dataset.barBorder || config.barBorder,
    "account-name": autoMountScript?.dataset.accountName || config.accountName,
    "account-role": autoMountScript?.dataset.accountRole || config.accountRole,
    "account-credits": autoMountScript?.dataset.accountCredits || String(config.accountCredits ?? ""),
    "account-validity": autoMountScript?.dataset.accountValidity || config.accountValidity,
    "account-permissions": autoMountScript?.dataset.accountPermissions || stringValue(config.accountPermissions),
    "admin-href": autoMountScript?.dataset.adminHref || config.adminHref,
    "printing-href": autoMountScript?.dataset.printingHref || config.printingHref,
    "login-href": autoMountScript?.dataset.loginHref || config.loginHref,
    "login-label": autoMountScript?.dataset.loginLabel || config.loginLabel
  };
  Object.entries(attributes).forEach(([name, value]) => {
    const keepsExplicitEmptyValue = (name === "allowed-apps" || name === "intro-available") && value !== undefined;
    if (value || keepsExplicitEmptyValue) element.setAttribute(name, value ?? "");
  });
  if (config.journeySnapshot !== undefined) {
    (element as HTMLElement & { journeySnapshot: readonly StarJourneyModuleSnapshotV2[] }).journeySnapshot = config.journeySnapshot;
  }
  document.body.prepend(element);

  const sessionEndpoint = autoMountScript?.dataset.sessionEndpoint || config.sessionEndpoint;
  const logoutEndpoint = autoMountScript?.dataset.logoutEndpoint || config.logoutEndpoint;
  const logoutRedirect = autoMountScript?.dataset.logoutRedirect || config.logoutRedirect || "/";
  if (!sessionEndpoint) return;

  void fetchSessionAccount(sessionEndpoint, {
    retryDelaysMs: delegatedLaunch ? [0, 250, 750, 1_500, 2_500] : [0],
  })
    .then((account) => {
      if (!account) return;
      element.setAttribute("account-name", account.name);
      element.setAttribute("account-role", account.role);
      if (account.credits !== undefined) element.setAttribute("account-credits", String(account.credits));
      else element.removeAttribute("account-credits");
      if (account.isAdmin) {
        const explicitAdmin = autoMountScript?.dataset.adminHref || config.adminHref;
        const production = /(^|\.)zr-eduai\.com$/.test(window.location.hostname)
          && !window.location.hostname.includes("-test.")
          && window.location.hostname !== "console-test.zr-eduai.com";
        const platformBase = autoMountScript?.dataset.starlinkBaseUrl
          || config.starlinkBaseUrl
          || (production ? "https://star.zr-eduai.com" : "https://console-test.zr-eduai.com");
        element.setAttribute("admin-href", explicitAdmin || `${platformBase.replace(/\/+$/, "")}/admin`);
      }
    })
    .catch(() => undefined);

  if (logoutEndpoint) {
    element.addEventListener("star-account-logout", () => {
      void fetch(logoutEndpoint, { method: "POST", credentials: "same-origin" })
        .finally(() => window.location.assign(logoutRedirect));
    });
  }
}

if (autoMountScript?.dataset.mountNavigation === "false") {
  // Importing this module still defines the shared navigation and launch elements.
} else if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", mount, { once: true });
} else {
  mount();
}
